import { useEffect, useRef, useState, useCallback } from "react";

interface SwipeHandlers {
  onSwipeLeft?: () => void;
  onSwipeRight?: () => void;
  onSwipeProgress?: (progress: number) => void;
}

interface SwipeState {
  isSwiping: boolean;
  direction: "left" | "right" | null;
  progress: number; // -1 to 1, negative = left, positive = right
}

export function useSwipe(
  handlers: SwipeHandlers,
  threshold: number = 50,
): SwipeState {
  const [swipeState, setSwipeState] = useState<SwipeState>({
    isSwiping: false,
    direction: null,
    progress: 0,
  });
  const startPos = useRef<{ x: number; y: number } | null>(null);
  const isDragging = useRef(false);

  const handleStart = useCallback((x: number, y: number) => {
    startPos.current = { x, y };
    isDragging.current = true;
  }, []);

  const handleMove = useCallback(
    (x: number, y: number) => {
      if (!startPos.current || !isDragging.current) return;

      const deltaX = x - startPos.current.x;
      const deltaY = y - startPos.current.y;

      // Only consider horizontal swipes
      if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 10) {
        const progress = Math.max(-1, Math.min(1, deltaX / 150));
        setSwipeState({
          isSwiping: true,
          direction: deltaX > 0 ? "right" : "left",
          progress,
        });
        handlers.onSwipeProgress?.(progress);
      }
    },
    [handlers],
  );

  const handleEnd = useCallback(
    (x: number, y: number) => {
      if (!startPos.current) return;

      const deltaX = x - startPos.current.x;
      const deltaY = y - startPos.current.y;

      // Only trigger if horizontal swipe is greater than vertical
      if (Math.abs(deltaX) > Math.abs(deltaY)) {
        if (deltaX > threshold && handlers.onSwipeRight) {
          handlers.onSwipeRight();
        } else if (deltaX < -threshold && handlers.onSwipeLeft) {
          handlers.onSwipeLeft();
        }
      }

      startPos.current = null;
      isDragging.current = false;
      setSwipeState({ isSwiping: false, direction: null, progress: 0 });
      handlers.onSwipeProgress?.(0);
    },
    [handlers, threshold],
  );

  useEffect(() => {
    // Touch events
    const handleTouchStart = (e: TouchEvent) => {
      handleStart(e.touches[0].clientX, e.touches[0].clientY);
    };

    const handleTouchMove = (e: TouchEvent) => {
      handleMove(e.touches[0].clientX, e.touches[0].clientY);
    };

    const handleTouchEnd = (e: TouchEvent) => {
      handleEnd(e.changedTouches[0].clientX, e.changedTouches[0].clientY);
    };

    // Mouse events for desktop swipe emulation
    const handleMouseDown = (e: MouseEvent) => {
      handleStart(e.clientX, e.clientY);
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging.current) {
        handleMove(e.clientX, e.clientY);
      }
    };

    const handleMouseUp = (e: MouseEvent) => {
      if (isDragging.current) {
        handleEnd(e.clientX, e.clientY);
      }
    };

    const handleMouseLeave = () => {
      if (isDragging.current) {
        startPos.current = null;
        isDragging.current = false;
        setSwipeState({ isSwiping: false, direction: null, progress: 0 });
        handlers.onSwipeProgress?.(0);
      }
    };

    document.addEventListener("touchstart", handleTouchStart);
    document.addEventListener("touchmove", handleTouchMove);
    document.addEventListener("touchend", handleTouchEnd);
    document.addEventListener("mousedown", handleMouseDown);
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
    document.addEventListener("mouseleave", handleMouseLeave);

    return () => {
      document.removeEventListener("touchstart", handleTouchStart);
      document.removeEventListener("touchmove", handleTouchMove);
      document.removeEventListener("touchend", handleTouchEnd);
      document.removeEventListener("mousedown", handleMouseDown);
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.removeEventListener("mouseleave", handleMouseLeave);
    };
  }, [handleStart, handleMove, handleEnd, handlers]);

  return swipeState;
}
